import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { commentsAPI } from '../../utils/api';

// Асинхронные действия
export const fetchComments = createAsyncThunk(
  'comments/fetchComments',
  async ({ contentType, contentId, limit = 50, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await commentsAPI.getComments(contentType, contentId, limit, offset);
      return { contentType, contentId, data: response.data };
    } catch (error) {
      console.error(`[fetchComments] Error fetching comments for ${contentType} ${contentId}:`, error);
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch comments');
    }
  }
);

export const createComment = createAsyncThunk(
  'comments/createComment',
  async ({ contentType, contentId, text, parentId = null }, { rejectWithValue }) => {
    try {
      const response = await commentsAPI.createComment({ contentType, contentId, text, parentId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create comment');
    }
  }
);

export const updateComment = createAsyncThunk(
  'comments/updateComment',
  async ({ commentId, text }, { rejectWithValue }) => {
    try {
      await commentsAPI.updateComment(commentId, { text });
      return { commentId, text };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update comment');
    }
  }
);

export const deleteComment = createAsyncThunk(
  'comments/deleteComment',
  async (commentId, { rejectWithValue }) => {
    try {
      await commentsAPI.deleteComment(commentId);
      return commentId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete comment');
    }
  }
);

export const likeComment = createAsyncThunk(
  'comments/likeComment',
  async (commentId, { rejectWithValue, getState }) => {
    try {
      await commentsAPI.likeComment(commentId);
      // Возвращаем commentId для обновления state
      return { commentId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to like comment');
    }
  }
);

export const unlikeComment = createAsyncThunk(
  'comments/unlikeComment',
  async (commentId, { rejectWithValue, getState }) => {
    try {
      await commentsAPI.unlikeComment(commentId);
      // Возвращаем commentId для обновления state
      return { commentId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to unlike comment');
    }
  }
);

const commentsSlice = createSlice({
  name: 'comments',
  initialState: {
    // Структура: { 'route-123': [...], 'video-456': [...] }
    commentsByContent: {},
    totals: {}, // { 'route-123': 50, 'video-456': 20 }
    isLoading: false,
    error: null,
    needsReload: {}, // { 'route-123': true, 'video-456': false } - флаг для перезагрузки
  },
  reducers: {
    // WebSocket события
    commentCreated: (state, action) => {
      const { comment } = action.payload;
      const key = `${comment.content_type}-${comment.content_id}`;
      
      if (!state.commentsByContent[key]) {
        state.commentsByContent[key] = [];
      }
      
      if (comment.parent_id) {
        // Это ответ - добавляем к родителю
        // Родитель может быть как основным комментарием, так и другим ответом
        const parentKey = `${comment.content_type}-${comment.content_id}`;
        const comments = state.commentsByContent[parentKey] || [];
        
        // Функция для рекурсивного поиска и обновления родительского комментария
        const addReplyToParent = (commentsList, parentId) => {
          // Ищем в основных комментариях
          const mainIndex = commentsList.findIndex(c => c.id === parentId);
          if (mainIndex !== -1) {
            const parentComment = commentsList[mainIndex];
            const existingReplyIndex = (parentComment.replies || []).findIndex(r => r.id === comment.id);
            if (existingReplyIndex === -1) {
              // Явно обновляем структуру для правильного отслеживания Immer
              commentsList[mainIndex] = {
                ...parentComment,
                replies: [...(parentComment.replies || []), comment],
                reply_count: (parentComment.reply_count || 0) + 1
              };
            }
            return true;
          }
          
          // Ищем в ответах (рекурсивно)
          for (let i = 0; i < commentsList.length; i++) {
            if (commentsList[i].replies && Array.isArray(commentsList[i].replies)) {
              // Проверяем, является ли этот ответ родителем
              const replyIndex = commentsList[i].replies.findIndex(r => r.id === parentId);
              if (replyIndex !== -1) {
                const parentReply = commentsList[i].replies[replyIndex];
                const existingReplyIndex = (parentReply.replies || []).findIndex(r => r.id === comment.id);
                if (existingReplyIndex === -1) {
                  // Явно обновляем структуру для правильного отслеживания Immer
                  // Клонируем весь массив replies, чтобы Immer правильно отследил изменения
                  commentsList[i] = {
                    ...commentsList[i],
                    replies: commentsList[i].replies.map((r, idx) => 
                      idx === replyIndex 
                        ? {
                            ...r,
                            replies: [...(r.replies || []), comment],
                            reply_count: (r.reply_count || 0) + 1
                          }
                        : r
                    )
                  };
                }
                return true;
              }
              
              // Рекурсивно ищем в ответах на ответы
              if (addReplyToParent(commentsList[i].replies, parentId)) {
                return true;
              }
            }
          }
          
          return false;
        };
        
        if (!addReplyToParent(comments, comment.parent_id)) {
          console.warn(`Parent comment ${comment.parent_id} not found in state for reply ${comment.id}. Will reload comments.`);
          // Устанавливаем флаг для перезагрузки комментариев
          state.needsReload[parentKey] = true;
        }
      } else {
        // Это основной комментарий - добавляем в начало
        state.commentsByContent[key].unshift(comment);
        state.totals[key] = (state.totals[key] || 0) + 1;
      }
    },
    
    commentUpdated: (state, action) => {
      const { comment } = action.payload;
      const key = `${comment.content_type}-${comment.content_id}`;
      
      if (state.commentsByContent[key]) {
        const index = state.commentsByContent[key].findIndex(c => c.id === comment.id);
        if (index !== -1) {
          state.commentsByContent[key][index] = {
            ...state.commentsByContent[key][index],
            ...comment,
          };
        } else {
          // Проверяем в ответах
          state.commentsByContent[key].forEach(parent => {
            if (parent.replies) {
              const replyIndex = parent.replies.findIndex(r => r.id === comment.id);
              if (replyIndex !== -1) {
                parent.replies[replyIndex] = { ...parent.replies[replyIndex], ...comment };
              }
            }
          });
        }
      }
    },
    
    commentDeleted: (state, action) => {
      const { commentId, contentType, contentId } = action.payload;
      const key = `${contentType}-${contentId}`;
      
      if (state.commentsByContent[key]) {
        const index = state.commentsByContent[key].findIndex(c => c.id === commentId);
        if (index !== -1) {
          state.commentsByContent[key].splice(index, 1);
          state.totals[key] = Math.max((state.totals[key] || 0) - 1, 0);
        } else {
          // Удаляем из ответов
          state.commentsByContent[key].forEach(parent => {
            if (parent.replies) {
              const replyIndex = parent.replies.findIndex(r => r.id === commentId);
              if (replyIndex !== -1) {
                parent.replies.splice(replyIndex, 1);
                parent.reply_count = Math.max(parent.reply_count - 1, 0);
              }
            }
          });
        }
      }
    },
    
    commentLiked: (state, action) => {
      // WebSocket события для лайков игнорируем - используем только fulfilled reducers
      // Это предотвращает конфликты между WebSocket и локальным state
    },
    
    commentUnliked: (state, action) => {
      // WebSocket события для лайков игнорируем - используем только fulfilled reducers
      // Это предотвращает конфликты между WebSocket и локальным state
    },
    
    clearComments: (state, action) => {
      const { contentType, contentId } = action.payload;
      const key = `${contentType}-${contentId}`;
      delete state.commentsByContent[key];
      delete state.totals[key];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchComments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        state.isLoading = false;
        const { contentType, contentId, data } = action.payload;
        const key = `${contentType}-${contentId}`;
        // Бэкенд возвращает { comments: [...], total: ... }
        const comments = data.comments || data.Comments || [];
        
        // Убеждаемся, что у каждого комментария есть массив replies (даже если пустой)
        // И что replies правильно структурированы
        const commentsWithReplies = comments.map(comment => {
          const replies = Array.isArray(comment.replies) ? comment.replies : [];
          // Минимальное логирование для отладки
          if (comment.reply_count > 0 && replies.length === 0) {
            console.warn(`Comment ${comment.id} has reply_count=${comment.reply_count} but replies array is empty`);
          }
          return {
            ...comment,
            replies,
          };
        });
        // Полностью заменяем комментарии (включая ответы) данными с сервера
        // Это гарантирует, что после обновления страницы мы видим актуальное состояние
        // Но сохраняем ответы, добавленные через WebSocket, если они еще не загружены с сервера
        const existingComments = state.commentsByContent[key] || [];
        const mergedComments = commentsWithReplies.map(serverComment => {
          // Ищем существующий комментарий в Redux
          const existingComment = existingComments.find(c => c.id === serverComment.id);
          
          // Всегда используем is_liked с сервера, так как он наиболее актуальный
          // Локальное состояние is_liked актуально только до обновления страницы
          
          if (existingComment && existingComment.replies && existingComment.replies.length > 0) {
            // Если у существующего комментария есть ответы, проверяем, не потеряли ли мы какие-то
            const serverReplyIds = new Set((serverComment.replies || []).map(r => r.id));
            
            // Находим ответы, которые есть в Redux, но нет на сервере (возможно, еще не закоммичены)
            const missingReplies = existingComment.replies.filter(r => !serverReplyIds.has(r.id));
            
            if (missingReplies.length > 0) {
              // Объединяем ответы с сервера и из Redux
              return {
                ...serverComment,
                replies: [...(serverComment.replies || []), ...missingReplies],
              };
            }
          }
          return serverComment;
        });
        
        state.commentsByContent[key] = mergedComments;
        state.totals[key] = data.total || data.Total || 0;
        // Сбрасываем флаг перезагрузки после успешной загрузки
        state.needsReload[key] = false;
      })
      .addCase(fetchComments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createComment.fulfilled, (state, action) => {
        // Комментарий уже добавлен через WebSocket
      })
      .addCase(updateComment.fulfilled, (state, action) => {
        // Комментарий уже обновлен через WebSocket
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        // Комментарий уже удален через WebSocket
      })
      .addCase(likeComment.pending, (state, action) => {
        // Оптимистичное обновление - обновляем UI немедленно
        const commentId = action.meta.arg;
        Object.keys(state.commentsByContent).forEach(key => {
          const updateComment = (c) => {
            if (c.id === commentId) {
              c.is_liked = true;
              c.like_count = (c.like_count || 0) + 1;
            }
            if (c.replies) {
              c.replies.forEach(updateComment);
            }
          };
          state.commentsByContent[key].forEach(updateComment);
        });
      })
      .addCase(likeComment.fulfilled, (state, action) => {
        // Успешно - state уже обновлен в pending
      })
      .addCase(likeComment.rejected, (state, action) => {
        // Откатываем изменения при ошибке
        const commentId = action.meta.arg;
        Object.keys(state.commentsByContent).forEach(key => {
          const updateComment = (c) => {
            if (c.id === commentId) {
              c.is_liked = false;
              c.like_count = Math.max((c.like_count || 0) - 1, 0);
            }
            if (c.replies) {
              c.replies.forEach(updateComment);
            }
          };
          state.commentsByContent[key].forEach(updateComment);
        });
      })
      .addCase(unlikeComment.pending, (state, action) => {
        // Оптимистичное обновление - обновляем UI немедленно
        const commentId = action.meta.arg;
        Object.keys(state.commentsByContent).forEach(key => {
          const updateComment = (c) => {
            if (c.id === commentId) {
              c.is_liked = false;
              c.like_count = Math.max((c.like_count || 0) - 1, 0);
            }
            if (c.replies) {
              c.replies.forEach(updateComment);
            }
          };
          state.commentsByContent[key].forEach(updateComment);
        });
      })
      .addCase(unlikeComment.fulfilled, (state, action) => {
        // Успешно - state уже обновлен в pending
      })
      .addCase(unlikeComment.rejected, (state, action) => {
        // Откатываем изменения при ошибке
        const commentId = action.meta.arg;
        Object.keys(state.commentsByContent).forEach(key => {
          const updateComment = (c) => {
            if (c.id === commentId) {
              c.is_liked = true;
              c.like_count = (c.like_count || 0) + 1;
            }
            if (c.replies) {
              c.replies.forEach(updateComment);
            }
          };
          state.commentsByContent[key].forEach(updateComment);
        });
      });
  },
});

export const {
  commentCreated,
  commentUpdated,
  commentDeleted,
  commentLiked,
  commentUnliked,
  clearComments,
} = commentsSlice.actions;

export default commentsSlice.reducer;

